import {format} from 'date-fns';
import {Button, IconButton} from 'platform/components';
import {
  Box,
  getValueByDevice,
  Hide,
  HStack,
  Show,
  Stack,
  Text,
  useDevice,
} from 'platform/foundation';
import {convertStringLocaleToDateFns, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import i18n from '@dms/i18n';

import {parseDate, pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface RentalResourceCalendarHeaderProps extends RequiredTestIdProps {
  currentDate: Date;
  onNavigate: (date: Date) => void;
}

export function RentalResourceCalendarHeader(props: RentalResourceCalendarHeaderProps) {
  const device = useDevice();
  const {language} = useLocale();

  const handlePrevious = () => {
    const newDate = parseDate(props.currentDate);
    newDate.setMonth(props.currentDate.getMonth() - MONTH_OFFSET);

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_navigate_previous_click',
      value: {
        newDate: newDate.toISOString(),
      },
    });

    props.onNavigate(newDate);
  };

  const handleNext = () => {
    const newDate = parseDate(props.currentDate);
    newDate.setMonth(props.currentDate.getMonth() + MONTH_OFFSET);

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_navigate_next_click',
      value: {
        newDate: newDate.toISOString(),
      },
    });

    props.onNavigate(newDate);
  };

  const onTodayClick = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_navigate_today_click',
    });
    props.onNavigate(new Date());
  };

  const title = match(device === 'mobile')
    .with(
      false,
      always(
        format(props.currentDate, 'LLLL yyyy', {locale: convertStringLocaleToDateFns(language)})
      )
    )
    .with(
      true,
      always(format(props.currentDate, 'LLLL', {locale: convertStringLocaleToDateFns(language)}))
    )
    .otherwise(always(null));

  return (
    <Stack
      data-testid={suffixTestId('header', props)}
      direction={['column', 'row']}
      align={['flex-start', 'center']}
      justify={['center', 'space-between']}
    >
      <HStack width="100%" spacing={2} align="center">
        <Hide onMobile>
          <Button
            data-testid={suffixTestId('header-todayButton', props)}
            variant="secondary"
            title={i18n.t('bigCalendar.today')}
            onClick={onTodayClick}
          />
        </Hide>
        <Show onMobile>
          <IconButton
            data-testid={suffixTestId('header-todayButton', props)}
            icon="action/calendar_today"
            priority="secondary"
            onClick={onTodayClick}
          />
        </Show>
        <IconButton
          data-testid={suffixTestId('header-chevronLeftButton', props)}
          icon="navigation/chevron_left"
          priority="secondary"
          onClick={handlePrevious}
        />
        <IconButton
          data-testid={suffixTestId('header-chevronRightButton', props)}
          icon="navigation/chevron_right"
          priority="secondary"
          onClick={handleNext}
        />
        <Box width={['100%', 'auto']}>
          <Text
            data-testid={suffixTestId('header-title', props)}
            size={getValueByDevice(device, 'base', 'large')}
          >
            {title}
          </Text>
        </Box>
      </HStack>
    </Stack>
  );
}

const MONTH_OFFSET = 1;
